// apps/web/src/pages/SearchPage.tsx
// 题库搜索页 — 当前题库内按关键词和题型检索

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getBank } from "@/api/banks";
import { searchQuestionsApi, type SearchResultItem } from "@/api/search";

type QuestionTypeFilter = "" | "single_choice" | "multiple_choice" | "true_false" | "fill_blank" | "essay";

const TYPE_TABS: { label: string; value: QuestionTypeFilter }[] = [
  { label: "全部", value: "" },
  { label: "单选题", value: "single_choice" },
  { label: "多选题", value: "multiple_choice" },
  { label: "判断题", value: "true_false" },
  { label: "填空题", value: "fill_blank" },
  { label: "简答题", value: "essay" },
];

const TYPE_LABEL: Record<string, string> = {
  single_choice: "单选题",
  multiple_choice: "多选题",
  true_false: "判断题",
  fill_blank: "填空题",
  essay: "简答题",
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const bankId = params.get("bankId") ?? undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  const [keyword, setKeyword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeType, setActiveType] = useState<QuestionTypeFilter>("");

  const { data: bank } = useQuery({
    queryKey: ["bank", bankId],
    queryFn: () => getBank(bankId!),
    enabled: !!bankId,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchTerm(keyword.trim());
    }, 280);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["search", searchTerm, bankId, activeType],
    queryFn: () =>
      searchQuestionsApi({
        q: searchTerm,
        page_size: 50,
        bank_id: bankId,
        type: activeType || undefined,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageTitle = bank?.name ?? (bankId ? "题库搜索" : "搜索题目");

  function submitSearch(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearchTerm(keyword.trim());
  }

  function clearKeyword() {
    setKeyword("");
    setSearchTerm("");
    inputRef.current?.focus();
  }

  return (
    <div className="min-h-screen bg-white pb-10">
      <div className="sticky top-0 z-20 bg-white">
        <div className="h-12 px-3 border-b border-gray-100 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center text-gray-700 btn-tap"
            aria-label="返回"
          >
            <ChevronLeftIcon />
          </button>
          <h1 className="mx-2 min-w-0 flex-1 text-center text-[16px] font-semibold text-gray-900 truncate">
            {pageTitle}
          </h1>
          <div className="w-16 h-9 rounded-full border border-gray-200 flex items-center justify-center gap-1.5 text-gray-700">
            <MoreIcon />
            <span className="w-px h-4 bg-gray-200" />
            <CircleIcon />
          </div>
        </div>

        <form onSubmit={submitSearch} className="px-4 py-3 flex items-center gap-3">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder={bankId ? "搜索当前题库试题" : "搜索题目内容、选项、解析"}
              className="w-full h-10 pl-9 pr-9 rounded-full bg-gray-100 text-[15px] text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-primary/20"
            />
            {keyword && (
              <button
                type="button"
                onClick={clearKeyword}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 btn-tap"
                aria-label="清空"
              >
                <CloseIcon />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="shrink-0 text-[15px] font-medium text-primary btn-tap"
          >
            搜索
          </button>
        </form>

        <div className="h-11 px-2 border-b border-gray-100 flex items-end overflow-x-auto">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => setActiveType(tab.value)}
              className={`relative min-w-[76px] flex-1 h-11 text-[15px] font-medium btn-tap ${
                activeType === tab.value ? "text-primary" : "text-gray-600"
              }`}
            >
              {tab.label}
              {activeType === tab.value && (
                <span className="absolute left-1/2 bottom-0 h-0.5 w-7 -translate-x-1/2 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>
      </div>

      <main>
        {isFetching ? (
          <ResultSkeleton />
        ) : isError ? (
          <ErrorState message={(error as Error).message} />
        ) : items.length === 0 ? (
          <EmptyState keyword={searchTerm} scoped={!!bankId} />
        ) : (
          <>
            <div className="px-4 py-2 text-xs text-gray-400 bg-surface">
              找到 <span className="font-medium text-primary">{total}</span> 道相关题目
            </div>
            <div className="divide-y divide-gray-100">
              {items.map((item) => (
                <SearchResultRow key={item.question.id} item={item} keyword={searchTerm} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function SearchResultRow({ item, keyword }: { item: SearchResultItem; keyword: string }) {
  const question = item.question;
  const matchingOptions = useMemo(
    () => (keyword ? (question.options ?? []).filter((option) => option.includes(keyword)).slice(0, 2) : []),
    [question.options, keyword]
  );

  return (
    <Link to={`/banks/${item.bank_id}/questions/${question.id}`} className="block px-4 py-4 bg-white btn-tap">
      <div className="flex items-start gap-1.5 text-[15px] leading-7 text-gray-900">
        <span className="shrink-0 font-medium text-primary">
          [{TYPE_LABEL[question.type] ?? question.type}]
        </span>
        <p className="min-w-0 flex-1">
          <HighlightedText text={question.content} keyword={keyword} />
        </p>
      </div>
      {matchingOptions.length > 0 && (
        <div className="mt-2 space-y-1 pl-1 text-sm leading-6 text-gray-500">
          {matchingOptions.map((option, index) => (
            <p key={`${option}-${index}`} className="line-clamp-2">
              <HighlightedText text={option} keyword={keyword} />
            </p>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-400">
        <span className="truncate">{item.bank_name}</span>
        {question.chapter && <span className="shrink-0 max-w-[45%] truncate">{question.chapter}</span>}
      </div>
    </Link>
  );
}

function HighlightedText({ text, keyword }: { text: string; keyword: string }) {
  const parts = useMemo(() => {
    if (!keyword) return [text];
    return text.split(new RegExp(`(${escapeRegExp(keyword)})`, "gi"));
  }, [text, keyword]);

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <mark key={`${part}-${index}`} className="bg-transparent px-0 text-rose-500 font-semibold">
            {part}
          </mark>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        )
      )}
    </>
  );
}

function EmptyState({ keyword, scoped }: { keyword: string; scoped: boolean }) {
  return (
    <div className="px-8 py-20 text-center text-gray-400">
      <p className="text-sm text-gray-500">{keyword ? `没有找到“${keyword}”相关题目` : "暂无题目"}</p>
      <p className="mt-1 text-xs">{scoped ? "可切换题型或换个关键词" : "换个关键词再试试"}</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="px-8 py-20 text-center">
      <p className="text-sm text-danger">搜索失败</p>
      <p className="mt-1 text-xs text-gray-400">{message}</p>
    </div>
  );
}

function ResultSkeleton() {
  return (
    <div className="divide-y divide-gray-100">
      {[1, 2, 3, 4, 5].map((item) => (
        <div key={item} className="px-4 py-4 bg-white">
          <div className="h-4 w-11/12 bg-gray-100 rounded animate-pulse" />
          <div className="mt-3 h-4 w-8/12 bg-gray-100 rounded animate-pulse" />
          <div className="mt-3 h-3 w-5/12 bg-gray-100 rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function SearchIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <circle cx="6" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="18" cy="12" r="1.5" />
    </svg>
  );
}

function CircleIcon() {
  return <span className="block w-4 h-4 rounded-full border-2 border-current" aria-hidden="true" />;
}
